import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'usehooks-ts';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  useGetApiArticlesReferencesQuery,
  useGetApiCategoriesArticleReferencesByIdQuery,
  useGetApiCategoryTreeQuery,
  useGetApiRecycleBinQuery,
  usePutApiCategoryTreeRecycleMutation,
} from '../../../redux/store/api/api';
import {
  selectActiveVersion,
  selectContextArticle,
  selectContextCategory,
  selectElementsToRycycleBinDialogOpened,
  setContextArticle,
  setContextCategory,
  setElementsToRecycleBinDialogOpened,
} from '../../../redux/store/content/slice';
import { addMessage } from '../../../redux/store/layout/slice';
import CustomDialog from '../../dialogs/CustomDialog';
import Loader from '../../loader/Loader';
import {
  EDIT_MODE_KEY,
  articleIdUrlParam,
  categoryIdUrlParam,
} from '../../../shared/constants';
import {
  useGetArticleIdsForElementIds,
  useGetCategoryIdsForElementIds,
} from '../../../hooks/useGetArticlesOrCategoriesForElementIds';

interface IElementsToRecycleBinDialogProps {
  selectedIds: string[];
}

function ElementsToRecycleBinDialog({
  selectedIds,
}: IElementsToRecycleBinDialogProps): JSX.Element {
  const { t: translation } = useTranslation();
  const dispatch = useAppDispatch();
  const dialogShow = useAppSelector(selectElementsToRycycleBinDialogOpened);
  const articles = useGetArticleIdsForElementIds(dialogShow ? selectedIds : []);
  const categories = useGetCategoryIdsForElementIds(
    dialogShow ? selectedIds : [],
  );
  const contextCategory = useAppSelector(selectContextCategory);
  const contextArticle = useAppSelector(selectContextArticle);
  const activeVersion = useAppSelector(selectActiveVersion);
  const [searchParams] = useSearchParams();
  const articleIdInParam: string | null = searchParams.get(articleIdUrlParam);
  const categoryIdInParam = searchParams.get(categoryIdUrlParam);
  const navigate = useNavigate();
  const { data: referencesOfArticle, refetch: refetchArticleReferences } =
    useGetApiArticlesReferencesQuery(
      articleIdInParam && activeVersion.isRecentVersion && activeVersion.id
        ? {
            articleId: articleIdInParam,
            versionId: activeVersion.id,
          }
        : skipToken,
    );
  const { refetch: refetchCategoryReferences } =
    useGetApiCategoriesArticleReferencesByIdQuery(
      categoryIdInParam
        ? {
            id: categoryIdInParam,
          }
        : skipToken,
    );
  const [moveArticleToRecycleBin, { isError, isLoading, error }] =
    usePutApiCategoryTreeRecycleMutation();
  const { refetch: refetchGetCategoriesTree } = useGetApiCategoryTreeQuery();
  const [editModeIsActive] = useLocalStorage<boolean>(EDIT_MODE_KEY, false);
  const { refetch: refetchGetCategoryRecycleBin } = useGetApiRecycleBinQuery(
    !editModeIsActive ? skipToken : undefined,
  );
  const referenceIdsOfActiveArticle = articleIdInParam
    ? referencesOfArticle?.resultObject?.articleReferenceLists
        ?.flat()
        .flatMap((l) => l.articleReferences) ?? []
    : [];
  const categoryReferenceIdsOfActiveArticle = referenceIdsOfActiveArticle
    .map((r) => r?.targetCategoryId)
    .filter((r) => r !== undefined && r !== null);
  const articleReferenceIdsOfActiveArticle = referenceIdsOfActiveArticle
    .map((r) => r?.targetArticleId)
    .filter((r) => r !== undefined && r !== null);

  useEffect(() => {
    if (isError) {
      dispatch(
        addMessage({
          id: 'MoveElementsToRecycleBinError',
          variant: 'danger',
          messageKeyBody:
            error && 'data' in error ? error.data?.messageKey : 'unknownError',
        }),
      );
    }
  }, [isError]);

  const handleMoveElementsToRecycleBin = (): void => {
    const articleIds = contextArticle.id ? [contextArticle.id] : articles;
    const categoryIds = contextCategory.id ? [contextCategory.id] : categories;
    moveArticleToRecycleBin({
      categoryTreeBatchOperationItems: {
        articleIds,
        categoryIds,
      },
    })
      .unwrap()
      .then((result) => {
        if (result.messageKey && result.messageKey !== '') {
          dispatch(
            addMessage({
              id: 'MoveElementsToRecycleBinSuccess',
              variant: 'success',
              messageKeyBody: result.messageKey,
            }),
          );
        }

        dispatch(setContextCategory({}));
        dispatch(setContextArticle({}));
        dispatch(setElementsToRecycleBinDialogOpened(false));
        refetchGetCategoriesTree();
        refetchGetCategoryRecycleBin();

        // close article after delete if it was deleted
        if (articleIdInParam && selectedIds.includes(articleIdInParam)) {
          navigate('/');
        }

        // refetch article references after delete if target of any ref on article was deleted
        if (
          articleIdInParam &&
          (categoryIds.some(
            (id) => categoryReferenceIdsOfActiveArticle?.includes(id),
          ) ||
            articleIds.some(
              (id) => articleReferenceIdsOfActiveArticle?.includes(id),
            ))
        ) {
          refetchArticleReferences();
        }

        // close category overview or refetch category references after delete
        if (categoryIdInParam) {
          if (selectedIds.includes(categoryIdInParam)) {
            navigate('/');
          } else {
            refetchCategoryReferences();
          }
        }
      });
  };

  return (
    <CustomDialog
      titleId='ElementToRecycleBinDialog'
      dialogTitle={translation('moveElementsToRecycleBin')}
      show={dialogShow}
      closeFunction={() => {
        dispatch(setContextCategory({}));
        dispatch(setContextArticle({}));
        dispatch(setElementsToRecycleBinDialogOpened(false));
      }}
      actionFunction={handleMoveElementsToRecycleBin}
      closeTitle={translation('cancel')}
      actionTitle={translation('moveToRecycleBin')}
      actionButtonVariant='outline-danger'
      actionButtonDisabled={isLoading}>
      <div aria-busy={isLoading}>
        {isLoading && <Loader />}
        {!isLoading && (
          <p>
            {translation(
              contextArticle.id || contextCategory.id
                ? 'shouldMoveSelectedElementToRecycleBin'
                : 'shouldMoveSelectedElementsToRecycleBin',
            )}
          </p>
        )}
      </div>
    </CustomDialog>
  );
}

export default ElementsToRecycleBinDialog;
